/**
 * Ordering for the host's booking-link list.
 *
 * <p>Kept out of the view so the "nothing upcoming" rule can be tested directly: it is the part
 * that is easy to get backwards, and getting it backwards buries the links that actually need
 * attention under the ones that don't.</p>
 */

export type SortKey = "creation" | "upcoming";

export interface SortableLink {
  createdAt: string;
  /** Soonest booking still ahead, or null when nothing is coming up. */
  nextBookingAt: string | null;
}

/**
 * Creation: newest first.
 *
 * <p>Soon upcoming: the most imminent booking first. Links with nothing coming up sort BELOW every
 * link that has something, and fall back to newest-first among themselves, so an empty link never
 * outranks a booked one whichever way its dates happen to compare.</p>
 */
export function compareLinks(a: SortableLink, b: SortableLink, sort: SortKey): number {
  if (sort === "upcoming" && (a.nextBookingAt || b.nextBookingAt)) {
    if (!a.nextBookingAt) return 1;
    if (!b.nextBookingAt) return -1;
    const byNext = a.nextBookingAt.localeCompare(b.nextBookingAt);
    if (byNext !== 0) return byNext;
  }
  return b.createdAt.localeCompare(a.createdAt);
}

export function sortLinks<T extends SortableLink>(items: T[], sort: SortKey): T[] {
  return [...items].sort((a, b) => compareLinks(a, b, sort));
}
